// Extracted from BatucadaRio.jsx — MappingEditorTab

import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as Tone from 'tone';
import {
  Play, Pause, Square, Volume2, Search, Download, Settings, ArrowLeft,
  Drum, Clock, SkipBack, Repeat, Layers, ChevronLeft, ChevronRight,
  Sliders, Grid3X3, FileAudio, Code, Package, FolderUp, Save, ChevronUp, ChevronDown,
  Trash2, X
} from 'lucide-react';
import { toast } from 'sonner';

// ============================================
// MAPPING EDITOR TAB (Interactive - Click to play, Drag to move)
// ============================================
export const MappingEditorTab = ({ sample, selectedKey, keyMappings, keyBuffers, keySettings, onSettingsChange, onRemapKey }) => {
  const [selectedZone, setSelectedZone] = useState(selectedKey);
  const [keyLow, setKeyLow] = useState('C2');
  const [keyHigh, setKeyHigh] = useState('C2');
  const [velLow, setVelLow] = useState(1);
  const [velHigh, setVelHigh] = useState(127);
  const [rootKey, setRootKey] = useState(selectedKey || 'C3');
  const [volume, setVolume] = useState(0);
  const [pan, setPan] = useState(0);
  const [tune, setTune] = useState(0);
  const [draggingKey, setDraggingKey] = useState(null); // The key being dragged
  const [playingKey, setPlayingKey] = useState(null);
  const activePlayerRef = useRef(null);
  const gridRef = useRef(null);

  // Update when selected key changes
  useEffect(() => {
    if (selectedKey) {
      setSelectedZone(selectedKey);
      setKeyLow(selectedKey);
      setKeyHigh(selectedKey);
      setRootKey(selectedKey);
      const settings = keySettings?.[selectedKey];
      if (settings) {
        setVolume(settings.volume ?? 0);
        setPan(settings.pan ?? 0);
        setTune(settings.tune ?? 0);
        setVelLow(settings.velLow ?? 1);
        setVelHigh(settings.velHigh ?? 127);
      }
    }
  }, [selectedKey, keySettings]);

  // Generate all keys for the grid (C1 to C7)
  const allNotes = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
  const octaves = [1, 2, 3, 4, 5, 6, 7];
  const allKeys = [];
  octaves.forEach(oct => {
    allNotes.forEach(note => {
      allKeys.push(`${note}${oct}`);
    });
  });

  // Get index of a key
  const getKeyIndex = (key) => allKeys.indexOf(key);
  const getKeyFromIndex = (idx) => allKeys[Math.max(0, Math.min(idx, allKeys.length - 1))];

  // Play a sample
  const playSample = async (key) => {
    const buffer = keyBuffers?.[key];
    const mapping = keyMappings?.[key];
    
    if (!buffer && !mapping) return;
    
    // Stop any currently playing
    if (activePlayerRef.current) {
      try {
        activePlayerRef.current.stop();
        activePlayerRef.current.dispose();
      } catch (e) { /* Ignore cleanup errors */ }
    }
    
    await Tone.start();
    await Tone.context.resume();
    
    setPlayingKey(key);
    
    try {
      let audioBuffer = buffer;
      
      // If no buffer but we have a mapping, try to load it
      if (!audioBuffer && mapping?.url) {
        const response = await fetch(mapping.url);
        const arrayBuffer = await response.arrayBuffer();
        const audioContext = Tone.getContext().rawContext;
        audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      }
      
      if (audioBuffer) {
        const player = new Tone.Player(audioBuffer).toDestination();
        player.start();
        activePlayerRef.current = player;
        
        // Auto-stop after playback
        setTimeout(() => {
          setPlayingKey(null);
          try {
            player.stop();
            player.dispose();
          } catch (e) { /* Ignore cleanup errors */ }
        }, (audioBuffer.duration * 1000) + 100);
      }
    } catch (err) {
      console.error('Failed to play sample:', err);
      setPlayingKey(null);
    }
  };

  // Handle zone click - play the sample
  const handleZoneClick = (key, e) => {
    e.stopPropagation();
    if (keyMappings[key]) {
      playSample(key);
      setSelectedZone(key);
    }
  };

  // Lock scroll position while dragging
  useEffect(() => {
    if (draggingKey && gridRef.current) {
      const grid = gridRef.current;
      const scrollPos = grid.scrollLeft;
      
      const preventScroll = (e) => {
        grid.scrollLeft = scrollPos;
      };
      
      grid.addEventListener('scroll', preventScroll);
      return () => grid.removeEventListener('scroll', preventScroll);
    }
  }, [draggingKey]);

  // Global mouse up to cancel drag if released outside grid
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (draggingKey) {
        // Restore scroll when done
        if (gridRef.current) {
          gridRef.current.style.overflow = 'auto';
        }
        setDraggingKey(null);
      }
    };
    
    window.addEventListener('mouseup', handleGlobalMouseUp);
    return () => window.removeEventListener('mouseup', handleGlobalMouseUp);
  }, [draggingKey]);

  const handleApply = () => {
    onSettingsChange?.(selectedKey, {
      ...keySettings?.[selectedKey],
      keyLow,
      keyHigh,
      velLow,
      velHigh,
      rootKey,
      volume,
      pan,
      tune
    });
  };

  // Visible range for the grid (show 3 octaves around the selected key)
  const selectedIdx = getKeyIndex(selectedKey) || 24;
  const startIdx = Math.max(0, selectedIdx - 18);
  const endIdx = Math.min(allKeys.length, selectedIdx + 30);
  const visibleKeys = allKeys.slice(startIdx, endIdx);

  return (
    <div className="space-y-3">
      {/* Instructions */}
      <div className="text-[10px] text-gray-500 bg-[#222] rounded px-3 py-1.5 flex items-center gap-4">
        <span>🖱️ <strong>Click</strong> a zone to play</span>
        <span>✋ <strong>Drag</strong> a zone to move it</span>
        <span className="text-amber-400">Playing: {playingKey || 'None'}</span>
      </div>

      {/* Top Controls Bar */}
      <div className="flex items-center gap-4 bg-[#2a2a2a] rounded p-2 text-[10px] flex-wrap">
        <span className="text-gray-400">K.Range:</span>
        <select 
          value={keyLow} 
          onChange={(e) => setKeyLow(e.target.value)}
          className="bg-[#333] text-white px-2 py-1 rounded text-[10px]"
        >
          {allKeys.map(k => <option key={k} value={k}>{k}</option>)}
        </select>
        <span className="text-gray-500">-</span>
        <select 
          value={keyHigh} 
          onChange={(e) => setKeyHigh(e.target.value)}
          className="bg-[#333] text-white px-2 py-1 rounded text-[10px]"
        >
          {allKeys.map(k => <option key={k} value={k}>{k}</option>)}
        </select>
        
        <span className="text-gray-400 ml-4">Velocity:</span>
        <input 
          type="number" 
          value={velLow} 
          onChange={(e) => setVelLow(Number(e.target.value))}
          min="1" max="127"
          className="w-12 bg-[#333] text-white px-2 py-1 rounded text-[10px]"
        />
        <span className="text-gray-500">-</span>
        <input 
          type="number" 
          value={velHigh} 
          onChange={(e) => setVelHigh(Number(e.target.value))}
          min="1" max="127"
          className="w-12 bg-[#333] text-white px-2 py-1 rounded text-[10px]"
        />
        
        <span className="text-gray-400 ml-4">Root:</span>
        <select 
          value={rootKey} 
          onChange={(e) => setRootKey(e.target.value)}
          className="bg-[#333] text-white px-2 py-1 rounded text-[10px]"
        >
          {allKeys.map(k => <option key={k} value={k}>{k}</option>)}
        </select>
        
        <span className="text-gray-400 ml-4">Vol:</span>
        <input 
          type="number" 
          value={volume} 
          onChange={(e) => setVolume(Number(e.target.value))}
          step="0.1"
          className="w-12 bg-[#333] text-white px-2 py-1 rounded text-[10px]"
        />
        <span className="text-gray-500">dB</span>
        
        <span className="text-gray-400 ml-2">Pan:</span>
        <input 
          type="number" 
          value={pan} 
          onChange={(e) => setPan(Number(e.target.value))}
          min="-100" max="100"
          className="w-10 bg-[#333] text-white px-2 py-1 rounded text-[10px]"
        />
        
        <span className="text-gray-400 ml-2">Tune:</span>
        <input 
          type="number" 
          value={tune} 
          onChange={(e) => setTune(Number(e.target.value))}
          step="0.01"
          className="w-12 bg-[#333] text-white px-2 py-1 rounded text-[10px]"
        />
        <span className="text-gray-500">st</span>
      </div>

      {/* Visual Mapping Grid */}
      <div className="bg-[#1a1a1a] rounded overflow-hidden">
        {/* Sample name bar */}
        <div className="bg-[#333] px-2 py-1 text-[10px] text-amber-400 flex items-center justify-between">
          <span>Sample: {sample?.name || 'None'}</span>
          <span className="text-gray-500">{selectedKey} {draggingKey ? `→ Moving ${keyMappings[draggingKey]?.name}` : ''}</span>
        </div>
        
        {/* Grid area - Velocity (Y) vs Keys (X) */}
        <div 
          ref={gridRef}
          className="relative h-40 bg-[#252525] overflow-x-auto"
          style={{ cursor: draggingKey ? 'grabbing' : 'default' }}
        >
          {/* Velocity axis labels */}
          <div className="absolute left-0 top-0 bottom-8 w-8 bg-[#1a1a1a] flex flex-col justify-between py-1 text-[8px] text-gray-500 z-10 items-end pr-1">
            <span>127</span>
            <span>96</span>
            <span>64</span>
            <span>32</span>
            <span>1</span>
          </div>
          
          {/* Grid with zones */}
          <div className="ml-8 h-full flex" style={{ minWidth: `${visibleKeys.length * 28}px` }}>
            {visibleKeys.map((key) => {
              const mapping = keyMappings[key];
              const isBlackKey = key.includes('#');
              const isSelected = key === selectedZone;
              const isPlaying = key === playingKey;
              const isBeingDragged = draggingKey === key;
              const isDropTarget = draggingKey && draggingKey !== key;
              
              return (
                <div 
                  key={key}
                  data-key={key}
                  className={`relative flex-shrink-0 w-7 h-full border-r border-[#333] 
                    ${isBlackKey ? 'bg-[#1a1a1a]' : 'bg-[#252525]'}
                    ${isDropTarget ? 'bg-blue-500/20' : ''}
                    transition-colors`}
                  onMouseUp={() => {
                    if (draggingKey && draggingKey !== key) {
                      onRemapKey?.(draggingKey, key);
                    }
                    if (draggingKey) {
                      // Restore scroll
                      if (gridRef.current) {
                        gridRef.current.style.overflow = 'auto';
                      }
                      setDraggingKey(null);
                    }
                  }}
                  onMouseEnter={() => {
                    if (draggingKey && draggingKey !== key) {
                      // Visual feedback when hovering over target
                    }
                  }}
                >
                  {/* Zone rectangle if mapped */}
                  {mapping && (
                    <div 
                      className={`absolute inset-x-0.5 rounded select-none
                        ${isBeingDragged ? 'opacity-50 ring-2 ring-blue-400 cursor-grabbing' : 'cursor-grab'}
                        ${isSelected ? 'bg-amber-500 ring-2 ring-white' : 'bg-amber-600/80 hover:bg-amber-500'}
                        ${isPlaying ? 'animate-pulse ring-2 ring-green-400' : ''}`}
                      style={{
                        top: `${100 - ((keySettings?.[key]?.velHigh || velHigh) / 127) * 100}%`,
                        bottom: `${((keySettings?.[key]?.velLow || velLow) / 127) * 100}%`,
                        minHeight: '20px',
                        touchAction: 'none'
                      }}
                      onClick={(e) => {
                        if (!draggingKey) {
                          handleZoneClick(key, e);
                        }
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        // Lock scroll position immediately
                        if (gridRef.current) {
                          gridRef.current.style.overflow = 'hidden';
                        }
                        setDraggingKey(key);
                      }}
                      title={`${mapping.name} - Click to play, hold & slide to move`}
                    >
                      <div className="text-[7px] text-black font-bold p-0.5 truncate text-center pointer-events-none">
                        {mapping.name?.slice(0, 6)}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          
          {/* Key labels at bottom */}
          <div className="absolute bottom-0 left-8 right-0 h-8 flex bg-[#1a1a1a] border-t border-[#444]">
            {visibleKeys.map((key) => {
              const isBlackKey = key.includes('#');
              const mapping = keyMappings[key];
              const note = key.replace(/[0-9]/g, '');
              const oct = key.replace(/[^0-9]/g, '');
              const showLabel = note === 'C';
              return (
                <div 
                  key={key}
                  className={`flex-shrink-0 w-7 flex flex-col items-center justify-center text-[8px] cursor-pointer
                    ${isBlackKey ? 'text-gray-600' : 'text-gray-400'}
                    ${mapping ? 'text-amber-400' : ''}
                    hover:bg-white/10`}
                  onClick={() => mapping && playSample(key)}
                >
                  {showLabel && <span className="font-bold">{key}</span>}
                  {!showLabel && mapping && <span>•</span>}
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Mini keyboard visualization */}
        <div className="h-10 flex ml-8 bg-[#111] border-t border-[#333]">
          {visibleKeys.map((key) => {
            const isBlackKey = key.includes('#');
            const mapping = keyMappings[key];
            const isSelected = key === selectedZone;
            const isPlaying = key === playingKey;
            return (
              <div 
                key={key}
                onClick={() => mapping && playSample(key)}
                className={`flex-shrink-0 w-7 border-r cursor-pointer transition-all
                  ${isBlackKey 
                    ? `bg-gray-900 border-gray-800 ${mapping ? 'bg-amber-900 hover:bg-amber-800' : 'hover:bg-gray-800'} ${isSelected ? 'bg-amber-700' : ''} ${isPlaying ? 'bg-green-700' : ''}` 
                    : `bg-gray-200 border-gray-300 ${mapping ? 'bg-amber-200 hover:bg-amber-300' : 'hover:bg-gray-100'} ${isSelected ? 'bg-amber-400' : ''} ${isPlaying ? 'bg-green-400' : ''}`
                  }
                  h-full flex items-end justify-center pb-0.5`}
                title={mapping ? `${mapping.name} - Click to play` : key}
              >
                {mapping && <span className="text-[6px] text-black/50">♪</span>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Apply Button */}
      <div className="flex justify-end">
        <button
          onClick={handleApply}
          className="px-4 py-1.5 bg-amber-500 hover:bg-amber-600 text-black text-xs font-medium rounded transition-colors"
        >
          Apply Mapping
        </button>
      </div>
    </div>
  );
};
